import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Users, CreditCard, ChevronRight, ChevronLeft, Calendar,
  Crown, LogOut, DollarSign, Zap, Loader2, BarChart3, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPayments = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('all');
  
  const limit = 20;

  const logout = useCallback(() => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  }, [navigate]);

  const fetchPayments = useCallback(async () => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit
      });
      
      if (filter !== 'all') params.append('payment_type', filter);
      
      const response = await axios.get(`${API}/api/admin/payments?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Backend returns 'transactions' but we use 'payments' in state
      setPayments(response.data.transactions || []);
      setTotal(response.data.total || 0);
      setSummary(response.data.summary);
    } catch (err) {
      console.error('Failed to fetch payments:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [token, page, filter, navigate, logout]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const totalPages = Math.ceil(total / limit);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentIcon = (type) => {
    switch (type) {
      case 'subscription':
        return <Crown className="w-4 h-4 text-amber-400" />;
      case 'boost':
        return <Zap className="w-4 h-4 text-blue-400" />;
      default:
        return <CreditCard className="w-4 h-4 text-slate-400" />;
    }
  };

  const getPaymentLabel = (type, tier) => {
    if (type === 'subscription') {
      return tier === 'platinum' ? 'Platin Premium' : tier === 'gold' ? 'Guld Premium' : 'Basis Premium';
    }
    if (type === 'boost') {
      return 'Profil Boost';
    }
    return type || 'Betaling';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Flirra Admin</h1>
              <p className="text-xs text-slate-400">Betalinger</p>
            </div>
          </div>
          
          <nav className="flex items-center gap-2">
            <Link to="/admin">
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">
                <BarChart3 className="w-4 h-4 mr-2" />
                Oversigt
              </Button>
            </Link>
            <Link to="/admin/users">
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">
                <Users className="w-4 h-4 mr-2" />
                Brugere
              </Button>
            </Link>
            <Link to="/admin/payments">
              <Button variant="ghost" size="sm" className="text-white bg-slate-700/50">
                <CreditCard className="w-4 h-4 mr-2" />
                Betalinger
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={logout}
              className="text-slate-400 hover:text-red-400 ml-4"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-5"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Omsætning</p>
                  <p className="text-2xl font-bold text-white mt-1">{summary.total_revenue} DKK</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-5"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Abonnementer</p>
                  <p className="text-2xl font-bold text-white mt-1">{summary.subscription_revenue} DKK</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-amber-400" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-5"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Boosts</p>
                  <p className="text-2xl font-bold text-white mt-1">{summary.boost_revenue} DKK</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-blue-400" />
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-slate-400 text-sm">
            Viser {payments.length} af {total} transaktioner
          </p>
          
          <Select value={filter} onValueChange={(v) => { setFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[180px] bg-slate-800/50 border-slate-700 text-white" data-testid="admin-payment-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle betalinger</SelectItem>
              <SelectItem value="subscription">Abonnementer</SelectItem>
              <SelectItem value="boost">Boosts</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Payments Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : payments.length === 0 ? (
          <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-10 text-center">
            <CreditCard className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Ingen betalinger fundet</p>
          </div>
        ) : (
          <div className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Type</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Bruger</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Beskrivelse</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Dato</th>
                    <th className="text-right text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Beløb</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/50">
                  {payments.map((payment, index) => (
                    <tr key={payment.id || index} className="hover:bg-slate-700/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getPaymentIcon(payment.type)}
                          <Badge className={`border-0 ${
                            payment.type === 'subscription' 
                              ? 'bg-amber-500/20 text-amber-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {payment.type === 'subscription' ? 'Abonnement' : 'Boost'}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {payment.user_name ? (
                          <div>
                            <p className="text-white text-sm">{payment.user_name}</p>
                            <p className="text-slate-400 text-xs">{payment.user_email}</p>
                          </div>
                        ) : (
                          <span className="text-slate-500">Ukendt</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-300 text-sm">
                          {getPaymentLabel(payment.type, payment.premium_tier)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-slate-400 text-sm">{formatDate(payment.created_at)}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-emerald-400 font-semibold">{payment.amount} DKK</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700/50">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="text-slate-400 hover:text-white"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Forrige
                </Button>
                <span className="text-slate-400 text-sm">
                  Side {page + 1} af {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="text-slate-400 hover:text-white"
                >
                  Næste
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPayments;
